import moment from 'moment';
import { GanttOptions, TooltipEventOption } from '@bringg-frontend/gantt-timeline';
import { TimeLineClusterItem, TimelineItem } from '@bringg-frontend/bringg-timeline';
import { HourFormat, Task } from '@bringg/types';
import { dateUtils } from '@bringg-frontend/utils';

import { DetailedRun } from '../stores/routes-monitoring-store';
import { routesMonitoringRootStore } from '../stores/routes-monitoring-root-store';

import styles from './tasks-timeline.module.scss';
import rowStyles from './tasks-timeline.module.scss';

const getTimelineOptions = (range: { from: string; to: string }, hourFormat: HourFormat): GanttOptions => {
	const { from, to } = range;
	const timeFormat = dateUtils.getMerchantTimeFormat(hourFormat);

	return {
		minAxisTime: moment(from),
		maxAxisTime: moment(to),
		displayStartTime: moment(from),
		displayEndTime: moment(to),
		showRemoveRowButton: false,
		groupHeightMode: 'fixed',
		tooltipBy: TooltipEventOption.Click,
		overlapItems: true, //in order for all items to be in one line
		stack: false,
		margin: { item: { horizontal: 0, vertical: +styles.timelineLinePadding }, axis: +styles.timelineLinePadding },
		formatXaxis: {
			hour: timeFormat,
			minute: timeFormat
		},
		fitClusterOnDoubleClick: false,
		groupTemplate: () => {
			// this is for setting timeline row height to be equal to table row height. the calculation is: 22 + $verticalMargin(4) * 2 + 2 * border(1) = 32
			const div = document.createElement('div');
			div.style.height = '22px';

			return div;
		},
		editable: false
	} as GanttOptions;
};

const addMarginToZoom = (startEnd: { start: number; end: number }) => {
	const { start, end } = startEnd;
	const interval = end - start;

	// apply a margin of 1% left and right of the data
	return { start: start - interval * 0.01, end: end + interval * 0.01 };
};

const runRangeItem = (run: DetailedRun): TimelineItem | null => {
	const { id, started_at, ended_at, scheduled_start_time, scheduled_end_time, eta_to_home } = run;
	let start = started_at,
		end = ended_at;

	if (!started_at) {
		start = scheduled_start_time;
		end = scheduled_end_time;
	} else if (!ended_at) {
		end = eta_to_home;
	}

	if (!start || !end) return null;

	return {
		id: `range-${id}`,
		rowId: id,
		backgroundItem: true,
		className: rowStyles.range,
		clickable: false,
		start: new Date(start).valueOf(),
		end: new Date(end).valueOf()
	};
};

const sortByPriority = (taskA: Task, taskB: Task) => taskA.priority - taskB.priority;

const getFirstTaskFromClusterItem = (item: TimeLineClusterItem) => {
	const { routesMonitoringStore } = routesMonitoringRootStore.getStore();

	return item.items
		.map(timelineItem => routesMonitoringStore.tasksByRun.get(timelineItem.rowId).get(timelineItem.id as number))
		.sort(sortByPriority)[0];
};

export const timelineProvider = {
	options: getTimelineOptions,
	addMarginToZoom,
	getRangeItem: runRangeItem,
	getFirstTaskFromClusterItem,
	sortByPriority
};
